import { TransformConvertorBase } from './transform.convertor';
import { ExecuteFlowletTransformation } from 'app/model/dataflow/executeflowlettransformation';
import { Transformation } from 'app/model/dataflow/transformation';
import { ExecuteFlowletPlainObject } from 'worker/app-workers/shared/plainObjects/executeFlowlet.plainObject';
import { AdapterContext, IResourceContext, Metamodel, Owned, ParameterValue, ReferenceAdapter } from 'shell-models-lib';
import { DataFlowExpressionUtils } from '../../expression.util';
import { ComplexTypeUtils } from 'app/authoring/component/transformations/complex-type-utils';
import { DataFlowMapColumn } from 'app/model/dataflow/dataflow-map.column';
import { ColumnReference } from 'app/model/dataset/columnReference';
import { DataFlowColumn } from 'app/model/dataset/dataflowcolumn';
import { DataFlowParameter } from 'app/model/dataflow/dataflow-parameter';

export class ExecuteFlowletConvertor extends TransformConvertorBase {
    private get executeFlowletTransform(): ExecuteFlowletTransformation {
        return this.transformation as ExecuteFlowletTransformation;
    }

    private get executeFlowletObject(): ExecuteFlowletPlainObject {
        return this.transformObject as ExecuteFlowletPlainObject;
    }

    public async convert(contextData: IResourceContext): Promise<Transformation> {
        await super.convert(contextData);

        this.executeFlowletObject.mappings.forEach((inputMappings, input) => {
            inputMappings.forEach((mapping) => {
                this._addColumnPlaceholder(
                    input,
                    mapping.alias,
                    mapping.name,
                    this.executeFlowletTransform,
                    mapping.isField,
                    mapping.isArraySelector
                );
            });
        });

        if (this.executeFlowletObject.composition) {
            // Adding proxy as any type to avoid circular dependency
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            this.executeFlowletTransform.flowlet = ReferenceAdapter.toProxyModel<any>(
                Metamodel.modelByName('flowlet'),
                {
                    referenceName: DataFlowExpressionUtils.extractStringConfigValue(
                        this.executeFlowletObject.composition
                    ),
                    type: 'DataFlowReference'
                }, AdapterContext.defaultContextOf(contextData)
            );
        }

        if (!this.executeFlowletObject.inputStreams.length) {
            this.executeFlowletTransform.isSource = true;
        }

        if (this.executeFlowletObject.parameters) {
            this.executeFlowletTransform.parameters = [];
            this.executeFlowletObject.parameters.forEach((parameterValueObject) => {
                const parameterValue = ParameterValue.MODEL.create<ParameterValue>();
                parameterValue.value = parameterValueObject.value;
                parameterValue.parameter = ReferenceAdapter.toProxyReferenceModel<DataFlowParameter>(
                    Metamodel.modelFor(DataFlowParameter),
                    parameterValueObject.name
                );
                this.executeFlowletTransform.parameters.push(parameterValue);
            });
        }

        super.postConvert();
        return this.transformation;
    }

    private _addColumnPlaceholder(
        input: number,
        aliasName: string,
        actualColumnName: string,
        transformation: ExecuteFlowletTransformation,
        isField: boolean,
        isArraySelector: boolean
    ): void {
        const fields = ComplexTypeUtils.splitComplexFieldPath(actualColumnName);
        const isComplexFieldPath = fields.length > 1 && isField;
        const additionalColumnPlaceholder = Owned.create<DataFlowColumn>(
            Metamodel.modelFor(DataFlowColumn),
            transformation
        );
        additionalColumnPlaceholder.name = actualColumnName;
        const placeHolderColumnRef = new ColumnReference(additionalColumnPlaceholder);
        if (isComplexFieldPath) {
            placeHolderColumnRef.complexFieldPath = actualColumnName;
            additionalColumnPlaceholder.name = fields[0];
        }
        if (isArraySelector && additionalColumnPlaceholder.name && additionalColumnPlaceholder.name.endsWith(']')) {
            additionalColumnPlaceholder.name = additionalColumnPlaceholder.name.substr(
                0,
                additionalColumnPlaceholder.name.length - 3
            );
        }
        const column = Owned.create<DataFlowMapColumn>(DataFlowMapColumn.MODEL, transformation);
        column.columnRef = placeHolderColumnRef;
        column.writeColumn = true;
        column.name = aliasName;
        column.outputAlias = aliasName;

        if (transformation.schemaMap.has(input)) {
            transformation.schemaMap.get(input).push(column);
        } else {
            transformation.schemaMap.set(input, [column]);
        }
    }
}
